/**
 * MatchHUD - The match heads-up display component.
 *
 * Responsibilities:
 * - Display the current player's life, gold, round number, and phase
 * - Display the preparation timer when in preparation phase
 * - React to backend state updates (GameStateUpdate, RoundStarted, etc.)
 * - Provide reactive render data for UI binding
 *
 * The MatchHUD reads state from AppShell (which holds the authoritative
 * GameStateService) and computes display data for the HUD overlay.
 */

import { AppShell, AppShellEvent } from '../AppShell';
import { PlayerData } from '../../shared/messages/MessageContracts';

// ─── Match HUD Render Data ──────────────────────────────────────────────────

/**
 * Data exposed for rendering the match HUD.
 * The actual DOM/React rendering is the responsibility of the view layer;
 * this component provides the reactive data.
 */
export interface MatchHUDRenderData {
  /** Whether the HUD should be visible (player is in a match). */
  isVisible: boolean;

  // ─── Current Player Stats ────────────────────────────────────────────────

  /** Current player's remaining life. */
  life: number;
  /** Current player's available gold. */
  gold: number;
  /** Whether the current player is alive. */
  isAlive: boolean;

  // ─── Match Info ──────────────────────────────────────────────────────────

  /** Current round number. */
  currentRound: number;
  /**
   * Current game phase for the HUD: 'preparation', 'combat', or the
   * current player's own phase ('eliminated'). Reads 'combat' while a
   * combat is streaming (see _buildRenderData — the per-player phase is
   * stale there).
   */
  phase: string;
  /** Whether the match is over. */
  isGameOver: boolean;
  /** Winner player ID (if match is over). */
  winnerId: string | null;

  // ─── Next Opponent ───────────────────────────────────────────────

  /**
   * The ID of the player the current player will fight in the NEXT combat
   * round (authoritative — PlayerData.nextOpponentId, fixed by the server
   * at the start of preparation). null when the current player sits out or
   * there is no upcoming combat. The view paints a red border on that
   * player's pill so the upcoming opponent is visible during prep.
   */
  nextOpponentId: string | null;

  // ─── Timer ───────────────────────────────────────────────────────────────

  /** Remaining preparation timer in seconds (null if not applicable). */
  preparationTimerSeconds: number | null;
  /** Whether the preparation phase is locked (timer expired). */
  isPreparationLocked: boolean;

  // ─── Spectator Info ──────────────────────────────────────────────────────

  /** Whether the current player is spectating a combat. */
  isSpectating: boolean;
  /** Player A of the spectated combat (null if not spectating). */
  spectatorPlayerAId: string | null;
  /** Player B of the spectated combat (null if not spectating). */
  spectatorPlayerBId: string | null;

  // ─── All Players Summary ─────────────────────────────────────────────────

  /** Summary data for all players in the match. */
  allPlayers: PlayerSummary[];
}

/**
 * Simplified player data for the HUD player list.
 */
export interface PlayerSummary {
  /** Player ID. */
  id: string;
  /**
   * Display name override (COOP mission bots carry their campaign
   * character name — "The Blight", "Siri the Siren", ...). null for
   * everyone else; the client renders the id when it is null.
   */
  displayName?: string | null;
  /** Remaining life. */
  life: number;
  /** Available gold. */
  gold: number;
  /** Whether the player is still alive. */
  isAlive: boolean;
  /** Current phase. */
  phase: string;
  /** Number of minions on the battlefield. */
  battlefieldSize: number;
  /** Number of cards in hand. */
  handSize: number;
}

/**
 * Callback invoked when the render data changes.
 */
export type MatchHUDRenderCallback = (data: MatchHUDRenderData) => void;

/**
 * Callback invoked when the match ends.
 */
export type MatchHUEndCallback = (winnerId: string | null) => void;

// ─── Match HUD ──────────────────────────────────────────────────────────────

export class MatchHUD {
  private _appShell: AppShell;
  private _playerId: string;
  private _renderCallbacks: MatchHUDRenderCallback[] = [];
  private _endCallbacks: MatchHUEndCallback[] = [];
  private _eventUnsubscribe: (() => void) | null = null;
  private _isVisible: boolean = false;

  constructor(appShell: AppShell) {
    this._appShell = appShell;
    this._playerId = appShell.playerId;
  }

  // ─── Public Accessors ────────────────────────────────────────────────────

  /** Returns the current render data (computed fresh from AppShell state). */
  get renderData(): MatchHUDRenderData {
    return this._buildRenderData();
  }

  /** Returns true if the HUD is currently visible/active. */
  get isVisible(): boolean {
    return this._isVisible;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  /**
   * Show the match HUD and start listening for state changes.
   */
  show(): void {
    if (this._isVisible) {
      return;
    }

    this._isVisible = true;

    // Listen for app shell events to trigger render updates
    this._eventUnsubscribe = this._appShell.onEvent(
      this._onAppEvent.bind(this)
    );

    // Initial render
    this._notifyRenderCallbacks();
  }

  /**
   * Hide the match HUD and stop listening for state changes.
   */
  hide(): void {
    if (!this._isVisible) {
      return;
    }

    this._isVisible = false;

    if (this._eventUnsubscribe) {
      this._eventUnsubscribe();
      this._eventUnsubscribe = null;
    }
  }

  // ─── Render Callbacks ────────────────────────────────────────────────────

  /**
   * Subscribe to render data changes.
   * Returns an unsubscribe function.
   */
  onRenderChange(callback: MatchHUDRenderCallback): () => void {
    this._renderCallbacks.push(callback);
    return () => {
      const index = this._renderCallbacks.indexOf(callback);
      if (index !== -1) {
        this._renderCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to match end events.
   * Returns an unsubscribe function.
   */
  onMatchEnd(callback: MatchHUEndCallback): () => void {
    this._endCallbacks.push(callback);
    return () => {
      const index = this._endCallbacks.indexOf(callback);
      if (index !== -1) {
        this._endCallbacks.splice(index, 1);
      }
    };
  }

  // ─── Internal Methods ────────────────────────────────────────────────────

  private _onAppEvent(event: AppShellEvent): void {
    switch (event.type) {
      case 'stateChange': {
        // Check if match ended
        const state = event.data as { match: { gameOver: boolean; winnerId: string | null } };
        if (state.match.gameOver) {
          this._notifyEndCallbacks(state.match.winnerId);
        }
        this._notifyRenderCallbacks();
        break;
      }
      case 'screenChange': {
        const data = event.data as { previousScreen: string; currentScreen: string };
        // Hide when navigating away from match screen
        if (data.currentScreen !== 'match' && this._isVisible) {
          this.hide();
        }
        break;
      }
    }
  }

  private _notifyRenderCallbacks(): void {
    const data = this._buildRenderData();
    for (const callback of this._renderCallbacks) {
      callback(data);
    }
  }

  private _notifyEndCallbacks(winnerId: string | null): void {
    for (const callback of this._endCallbacks) {
      callback(winnerId);
    }
  }

  private _buildRenderData(): MatchHUDRenderData {
    const matchState = this._appShell.gameState.match;
    const allPlayers = matchState.players;

    // Find current player's data
    const currentPlayer = allPlayers.find((p) => p.id === this._playerId) ?? null;

    // Player stats (defaults if player data unavailable)
    const life = currentPlayer?.life ?? 0;
    const gold = currentPlayer?.gold ?? 0;
    const isAlive = currentPlayer?.isAlive ?? false;
    // The PER-PLAYER phase is stale during combat: the server flips every
    // player to 'combat' in endPreparationPhase but never broadcasts that
    // (the only GameStateUpdate after combat already carries the next
    // round's 'preparation'), so a player's phase field keeps reading
    // 'preparation' while the combat animates. The MATCH-level phase is
    // live: the client sets it to 'combat' on CombatStarted and the
    // post-combat GameStateUpdate resets it — prefer it while in combat
    // so the HUD phase label (and the settings-button visibility) tracks
    // the real phase. Outside combat, the player's own phase wins (it is
    // the one that can say 'eliminated').
    const phase = matchState.phase === 'combat'
      ? 'combat'
      : (currentPlayer?.phase ?? matchState.phase);

    // Preparation timer
    const preparationTimerSeconds = matchState.preparationTimerSeconds;
    const isPreparationLocked = phase === 'combat' || preparationTimerSeconds === 0;

    // Spectator info
    const isSpectating = matchState.isSpectating;
    const spectatorPlayerAId = matchState.spectatorCombatPlayerAId;
    const spectatorPlayerBId = matchState.spectatorCombatPlayerBId;

    // Build player summaries
    const playerSummaries: PlayerSummary[] = allPlayers.map((p) => ({
      id: p.id,
      displayName: p.displayName ?? null,
      life: p.life,
      gold: p.gold,
      isAlive: p.isAlive,
      phase: p.phase,
      battlefieldSize: p.battlefield.length,
      handSize: p.hand.length,
    }));

    return {
      isVisible: this._appShell.isInMatch,
      life,
      gold,
      isAlive,
      currentRound: matchState.currentRound,
      phase,
      isGameOver: matchState.gameOver,
      winnerId: matchState.winnerId,
      nextOpponentId: currentPlayer?.nextOpponentId ?? null,
      preparationTimerSeconds,
      isPreparationLocked,
      isSpectating,
      spectatorPlayerAId,
      spectatorPlayerBId,
      allPlayers: playerSummaries,
    };
  }
}
